import { useMutation } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Icon } from '@/components/primitives/icon/Icon';
import { Input } from '@/components/primitives/Input';
import { SubmitButton } from '@/components/primitives/SubmitButton';
import { AutoTooltip } from '@/components/primitives/AutoTooltip';
import { SearchDropdown } from '@/components';
import { closeDialog, setDialog } from '@/redux/reducers/dialog';
import { useAppDispatch } from '@/redux/hooks';
import clsx from 'clsx';
import { CREATE_BUILDING_MUTATION } from '@/components/sidebar/mutations/createSiteAndBuilding';
import { SidebarMenuSiteQuery } from './SidebarMenu';
import { useGetAllSitesQuery, useGetBuildingsQuery } from '@/.generated/graphql';
import { useSRZId } from '@/hooks';
import { useGetFloorQuery } from '../DetailsPanel/getFloor';

export function CreateBuildingDialog() {
  const dispatch = useAppDispatch();
  const [createBuilding] = useMutation(CREATE_BUILDING_MUTATION, {
    refetchQueries: [{ query: SidebarMenuSiteQuery }],
  });
  const { data: sitesData } = useGetAllSitesQuery({});
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    clearErrors,
    formState: { isValid, isDirty, errors },
  } = useForm({
    defaultValues: {
      name: '',
      siteId: '',
      address: {
        lines: ['', '', ''],
        country: '',
      },
    },
    mode: 'onBlur',
  });

  const { buildingId: currentBuilding, spaceId } = useSRZId();
  const { data: space } = useGetFloorQuery({ floorId: spaceId });
  const { data: buildingData } = useGetBuildingsQuery({
    variables: { buildingId: currentBuilding! },
    skip: !currentBuilding,
  });
  const initialSiteId = spaceId ? space?.building.site.id : buildingData?.building?.site.id;

  const initialSetupDone = React.useRef(false);
  useEffect(() => {
    if (initialSiteId && !initialSetupDone.current) {
      setValue('siteId', initialSiteId, { shouldDirty: true, shouldValidate: true });
      initialSetupDone.current = true;
    }
  }, [initialSiteId, setValue]);

  const formSiteId = watch('siteId');
  const selectedSite = sitesData?.sites?.data.find((site) => site.id === formSiteId);
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(async (values) => {
        if (!values.siteId) {
          setError('siteId', { type: 'required', message: "Parent site can't be empty." });
          return;
        }
        setLoading(true);
        datadogRum.addAction('Create building', { source: 'sites-and-buildings' });
        try {
          await createBuilding({
            variables: {
              input: {
                name: values.name,
                siteId: values.siteId,
                address: values.address,
              },
            },
          });

          toast.success(`Building "${values.name}" created successfully`, { duration: 5000 });
          dispatch(closeDialog());
        } catch (error) {
          console.error('Error creating building:', error);
          toast.error('Error creating building', { duration: 5000 });
        } finally {
          setLoading(false);
        }
      })}
    >
      <h1 className="text-base text-gray-900">Add A New Building</h1>

      <div className="w-full mb-3 align-top">
        <span className="text-product-gray900 text-sm ml-0 mb-2">Assign to Site:</span>
        <SearchDropdown
          id="siteDropdown"
          size="sm"
          options={sitesData?.sites?.data ?? []}
          optionToSearchValue={(option) => (option ? option.name : '')}
          renderOption={(option) => option.name}
          placeholder="Site Name"
          className={clsx('w-full py-2', errors.siteId && 'border-red-500')}
          onClear={() => {
            setValue('siteId', '', { shouldDirty: true });
            setError('siteId', { type: 'required', message: "Parent site can't be empty." });
          }}
          onChange={(value) => {
            if (value) {
              setValue('siteId', value.id, { shouldDirty: true, shouldValidate: true });
              clearErrors('siteId');
            }
          }}
          value={selectedSite}
        />
        {errors.siteId && <div className="text-red-500 text-xs">{errors.siteId.message}</div>}
      </div>

      <hr className="border-t border-gray-200 my-2" />

      <div className="w-full py-2">
        <span className="text-product-gray900 text-sm ml-0 mb-2">Name:</span>
        <Input
          type="text"
          label=""
          wrapperClassName="w-full"
          variant="narrow"
          errorMessage={errors.name && errors.name.message}
          className={errors.name ? 'border-red-500' : ''}
          {...register('name', { required: 'Please enter a building name.' })}
        />
      </div>
      <div className="w-full py-2">
        <span className="text-product-gray900 text-sm ml-0 mb-1 block">Address:</span>
        <Input
          type="text"
          {...register('address.lines.0', { required: "Address can't be empty." })}
          errorMessage={errors.address?.lines?.[0]?.message || errors.address?.message}
          label=""
          placeholder={t('address')}
          variant="narrow"
          wrapperClassName="w-full"
          className={errors.address?.lines?.[0] ? 'border-red-500' : ''}
        />
      </div>

      <div className="w-full py-2">
        <span className="text-product-gray900 text-sm ml-0 mb-1 block">City/State/Zip:</span>
        <Input
          type="text"
          {...register('address.lines.1', { required: "City/state/zip can't be empty." })}
          errorMessage={errors.address?.lines?.[1]?.message || errors.address?.message}
          label=""
          placeholder="City, State ZIP"
          variant="narrow"
          wrapperClassName="w-full"
          className={errors.address?.lines?.[1] ? 'border-red-500' : ''}
        />
      </div>

      <div className="flex justify-end gap-4">
        <button
          type="button"
          className="rounded-md px-4 py-1 text-sm text-product-gray900 hover:bg-product-gray50 border border-gray-200"
          onClick={() => dispatch(closeDialog())}
        >
          Cancel
        </button>
        <SubmitButton loading={loading} loadingContent="Creating" disabled={!isValid || !isDirty}>
          Create Building
        </SubmitButton>
      </div>
    </form>
  );
}

export function CreateBuildingButton({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const onClick = async () =>
    dispatch(
      setDialog({
        content: <CreateBuildingDialog />,
        options: {
          className: 'w-[520px] rounded-[15px]',
        },
      }),
    );
  const isCompact = variant === 'compact';
  return (
    <AutoTooltip label="Create Building">
      <button
        onClick={onClick}
        className={clsx(
          'whitespace-nowrap rounded flex items-center text-sm justify-center text-product-gray900 hover:bg-product-gray50 border border-gray-200',
          variant === 'default' ? 'py-2 px-4' : '',
          isCompact ? 'py-2 px-2 mb-4' : '',
        )}
      >
        <Icon name="plus" className={clsx('w-4', { 'mr-2': !isCompact })} color="black"></Icon>
        {!isCompact && t('create')}
      </button>
    </AutoTooltip>
  );
}
